/**
 * InvoiceDetail.jsx — Invoice detail view with payment recording
 *
 * Shows:
 * - Header: number, status badge, type
 * - Client, job reference
 * - Amount breakdown: total, paid, balance due
 * - Payment terms, due date
 * - Payment history (from payments jsonb array)
 * - Record Payment button → form
 * - Status actions: Mark Sent, Mark Paid, Void
 * - Delete button
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtC, fmtD, INVOICE_STATUS_COLORS, toLocalDateStr, paymentDate } from '../../lib/utils';
import { dueDateInDays, parseTermsDays, finalTermsDays } from '../../lib/invoiceDefaults';
import { navigateToJob } from '../../lib/crossNav';
import { openEmail, buildEmail, buildMailtoUrl } from '../../lib/emailTemplates';
import { useAuth } from '../../hooks/useAuth';
import { useInvoices, useUpdateInvoice, useDeleteInvoice } from '../../hooks/useInvoices';
import { useJobs, useUpdateJob } from '../../hooks/useJobs';
import { useContacts } from '../../hooks/useContacts';
import { useSettings } from '../../hooks/useSettings';
import { useRBAC } from '../../hooks/useRBAC';
import { useXeroStatus, useSyncInvoiceToXero, useUploadInvoicePdfToXero } from '../../hooks/useXero';
import { useToast } from '../layout/Toast';
import { generateInvoicePdf } from '../../lib/pdfGenerator';
import { pushInvoicePdfToXero } from '../../lib/invoicePdfSync';
import PdfPreviewModal from '../common/PdfPreviewModal';
import FocusHide from '../shared/FocusHide';
import { useFocus } from '../../hooks/useFocus';

export default function InvoiceDetail({ invoice, onClose }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const updateMutation = useUpdateInvoice();
  const deleteMutation = useDeleteInvoice();
  const { data: settings } = useSettings();
  const { hasAccess } = useRBAC();
  // manage_own deletes invoices on jobs you are or were in the pay split for
  // (the server checks every covered job and says why on a 403); full deletes
  // any, incl. unlinked. 'create' can create/edit/send only.
  // Below full the server also refuses invoices with money on them (and ones
  // un-paid since — that part only it can see); hide the button for the visible case.
  const hasMoney = ['paid', 'partial'].includes(invoice?.status) || (parseFloat(invoice?.paidAmount) || 0) > 0
    || (Array.isArray(invoice?.payments) && invoice.payments.some((p) => !p.voided));
  const canDelete = hasAccess('invoices', 'full', settings)
    || (hasAccess('invoices', 'manage_own', settings) && !!(invoice?.jobId || invoice?.jobIds?.length) && !hasMoney);
  const { profile } = useAuth();
  const senderName = profile?.full_name || '';
  const { data: allJobs = [] } = useJobs();
  const { data: allContacts = [] } = useContacts();
  const updateJobMutation = useUpdateJob();
  const linkedJob = invoice?.jobId ? allJobs.find(j => j.id === invoice.jobId) : null;
  // Bulk (multi-job) invoice: covered jobs live in jobIds, breakdown in lineItems.
  const isBulk = Array.isArray(invoice?.jobIds) && invoice.jobIds.length > 0;
  const coveredJobs = isBulk ? invoice.jobIds.map(id => allJobs.find(j => j.id === id)).filter(Boolean) : [];
  const linkedContact = invoice?.contactId ? allContacts.find(c => c.id === invoice.contactId) : null;
  // An invoice pinned to another contact than its job's (issued before the job's
  // client was changed) goes to that contact only, never to the job's new client.
  const pinnedElsewhere = !!invoice?.contactId && !!linkedJob && invoice.contactId !== linkedJob.contactId;
  const clientEmail = pinnedElsewhere
    ? (linkedContact?.email || '')
    : (linkedJob?.contactEmail || linkedContact?.email || coveredJobs[0]?.contactEmail || '');

  // Move every covered job to `target` when the bulk invoice is sent/paid.
  // The client-side autoStatus never sees a job-less invoice (it reads
  // /invoices?job_id=), so the flips happen here explicitly. For 'paid',
  // a job with its own outstanding non-extra invoice stays put (autoStatus
  // parity: paid means everything owed on the job is settled).
  const { data: allInvoicesForFlip = [] } = useInvoices();
  const flipCoveredJobs = async (target) => {
    if (!isBulk) return;
    const from = target === 'paid'
      ? ['delivered', 'partial_delivery', 'invoiced']
      : ['delivered', 'partial_delivery'];
    const hasOwnOutstanding = (jobId) => allInvoicesForFlip.some(inv =>
      inv.jobId === jobId &&
      !['paid', 'void'].includes(inv.status) &&
      (inv.invoiceType || 'full') !== 'extra'
    );
    for (const j of coveredJobs.filter(cj => from.includes(cj.status))) {
      if (target === 'paid' && hasOwnOutstanding(j.id)) continue;
      try {
        await updateJobMutation.mutateAsync({ id: j.id, status: target });
      } catch (err) {
        console.error('Bulk invoice: job status flip failed', j.id, err);
      }
    }
  };

  const { data: xeroStatus } = useXeroStatus();
  const xeroConnected = xeroStatus?.connected === true;
  const syncMutation = useSyncInvoiceToXero();
  const uploadXeroPdfMutation = useUploadInvoicePdfToXero();
  // No auto-check on open — this called useCheckXeroPayments and never read
  // the result, spending a Xero API call every time an invoice was viewed.
  // The background poller covers it; Sync gives an immediate answer.

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'check',
    date: toLocalDateStr(new Date()),
  });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [pdfPreview, setPdfPreview] = useState(null);
  const { isHidden } = useFocus();

  if (!invoice) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-dim text-sm">Select an invoice to view details</p>
      </div>
    );
  }

  const balance = Math.round(((parseFloat(invoice.amount) || 0) - (parseFloat(invoice.paidAmount) || 0)) * 100) / 100;
  const statusColor = INVOICE_STATUS_COLORS[invoice.status] || '#999';
  const payments = invoice.payments || [];
  // Focus can empty the Status Actions row (only on a void invoice) — drop it then.
  const statusActionsEmpty = invoice.status === 'void' && isHidden('invoice.markSent')
    && (balance > 0 || isHidden('invoice.editMarkPaid'));

  // ── Handlers ──────────────────────────────────────────────────────────────

  const startEditing = () => {
    setEditForm({
      amount: String(invoice.amount || ''),
      terms: invoice.terms || 'Net 30',
      dueDate: invoice.dueDate || '',
      notes: invoice.notes || '',
    });
    setEditing(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const newAmount = parseFloat(editForm.amount);
    if (isNaN(newAmount) || newAmount <= 0) {
      toast('Amount must be greater than zero', 'error');
      return;
    }
    const paidAmt = parseFloat(invoice.paidAmount) || 0;
    if (newAmount < paidAmt) {
      toast(`Amount cannot be less than already paid (${fmtC(paidAmt)})`, 'error');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: invoice.id,
        amount: newAmount,
        terms: editForm.terms,
        dueDate: editForm.dueDate,
        notes: editForm.notes.trim(),
      });
      toast('Invoice updated');
      setEditing(false);
      if (xeroConnected && invoice.xeroInvoiceId) syncMutation.mutate(invoice.id);
    } catch (err) {
      toast('Failed to update invoice', 'error');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();

    const amount = parseFloat(paymentForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast('Please enter a valid amount', 'error');
      return;
    }

    if (amount > balance) {
      toast('Payment cannot exceed balance due', 'error');
      return;
    }

    const newPayments = [
      ...payments,
      {
        date: paymentForm.date,
        amount,
        method: paymentForm.method,
      },
    ];

    const newPaidAmount = Math.round(((parseFloat(invoice.paidAmount) || 0) + amount) * 100) / 100;

    try {
      await updateMutation.mutateAsync({
        id: invoice.id,
        paidAmount: newPaidAmount,
        payments: newPayments,
        status: newPaidAmount >= (parseFloat(invoice.amount) || 0) ? 'paid' : newPaidAmount > 0 ? 'partial' : invoice.status,
      });

      toast('Payment recorded');
      setShowPaymentForm(false);
      setPaymentForm({ amount: '', method: 'check', date: toLocalDateStr(new Date()) });
      if (xeroConnected && invoice.xeroInvoiceId) syncMutation.mutate(invoice.id);
      if (newPaidAmount >= (parseFloat(invoice.amount) || 0)) await flipCoveredJobs('paid');
    } catch (err) {
      toast('Failed to record payment', 'error');
    }
  };

  const handleVoidPayment = async (index) => {
    if (!confirm(`Void payment #${index + 1} (${fmtC(payments[index].amount)})?`)) return;

    const voidedPayment = { ...payments[index], voided: true, voidedAt: new Date().toISOString() };
    const newPayments = payments.map((p, i) => (i === index ? voidedPayment : p));
    const newPaidAmount = Math.round(newPayments.filter(p => !p.voided).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) * 100) / 100;

    try {
      await updateMutation.mutateAsync({
        id: invoice.id,
        paidAmount: newPaidAmount,
        payments: newPayments,
        status: newPaidAmount >= (parseFloat(invoice.amount) || 0) ? 'paid' : newPaidAmount > 0 ? 'partial' : 'unpaid',
      });
      toast('Payment voided');
      if (xeroConnected && invoice.xeroInvoiceId) syncMutation.mutate(invoice.id);
    } catch (err) {
      toast('Failed to void payment', 'error');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateMutation.mutateAsync({
        id: invoice.id,
        status: newStatus,
        ...(newStatus === 'sent' && !invoice.sentAt && { sentAt: new Date().toISOString() }),
      });
      toast(`Invoice marked as ${newStatus}`);
      if (xeroConnected && invoice.xeroInvoiceId) syncMutation.mutate(invoice.id);
      if (newStatus === 'paid') await flipCoveredJobs('paid');
      if (newStatus === 'sent') await flipCoveredJobs('invoiced');
    } catch (err) {
      toast('Failed to update status', 'error');
    }
  };

  const handleDelete = async () => {
    if (invoice.xeroInvoiceId) {
      toast('Void this invoice instead — it\'s synced to Xero', 'error');
      return;
    }
    if (!confirm('Delete this invoice? It moves to Trash for 30 days, and an admin can restore it from Settings ▸ Trash until then.')) return;

    try {
      await deleteMutation.mutateAsync(invoice.id);
      toast('Invoice moved to Trash');
      onClose();
    } catch (err) {
      toast(`Failed to delete invoice: ${err.message}`, 'error');
    }
  };

  return (
    <div data-track-area="invoice-detail" className="card p-6 flex flex-col gap-6 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
        <div className="flex-1">
          <h2 className="text-2xl font-heading text-text-primary mb-1">Invoice {invoice.number}</h2>
          <p className="text-sm text-text-dim">{invoice.client || 'N/A'}</p>
          {invoice.jobId && (
            <button
              onClick={() => navigateToJob(navigate, invoice.jobId)}
              className="text-2xs text-accent hover:underline text-left mt-0.5"
              title="Go to job"
            >
              View Job →
            </button>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span
            className="inline-block px-3 py-1 rounded text-xs font-semibold border"
            style={{
              backgroundColor: statusColor + '20',
              borderColor: statusColor + '40',
              color: statusColor,
            }}
          >
            {invoice.status.toUpperCase()}
          </span>
          <span className="px-3 py-1 rounded text-xs bg-surface border border-border text-text-dim">
            {isBulk ? `bulk · ${invoice.jobIds.length} jobs` : (invoice.invoiceType || 'full')}
          </span>
          {!editing && invoice.status !== 'void' && invoice.status !== 'paid' && (
            <FocusHide k="invoice.editMarkPaid">
              <button
                onClick={startEditing}
                className="px-3 py-1 rounded text-xs bg-accent/10 border border-accent/30 text-accent font-semibold hover:bg-accent/20 transition-colors"
              >
                Edit
              </button>
            </FocusHide>
          )}
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <form onSubmit={handleSaveEdit} className="bg-surface border border-accent/30 rounded p-4 space-y-3">
          <div className="text-xs font-semibold text-accent mb-2">Edit Invoice</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-2xs font-semibold text-text-dim mb-1">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={editForm.amount}
                disabled={isBulk}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                className="w-full px-2 py-1.5 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors disabled:opacity-50"
                title={isBulk ? 'Bulk invoice amounts come from the covered jobs' : undefined}
              />
              {isBulk && (
                <div className="text-2xs text-text-dim mt-0.5">Fixed — sum of the covered jobs</div>
              )}
            </div>
            <div>
              <label className="block text-2xs font-semibold text-text-dim mb-1">Terms</label>
              <select
                value={editForm.terms}
                onChange={(e) => setEditForm({ ...editForm, terms: e.target.value })}
                className="w-full px-2 py-1.5 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
              >
                <option value="Due on receipt">Due on receipt</option>
                <option value="Net 7">Net 7</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-2xs font-semibold text-text-dim mb-1">Due Date</label>
            <input
              type="date"
              value={editForm.dueDate}
              onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
              className="w-full px-2 py-1.5 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
            />
          </div>
          <div>
            <label className="block text-2xs font-semibold text-text-dim mb-1">Notes</label>
            <input
              type="text"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              className="w-full px-2 py-1.5 bg-bg border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1 px-3 py-1.5 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex-1 px-3 py-1.5 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Amount Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
        <div className="card bg-surface p-4">
          <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Total</div>
          <div className="text-xl font-heading text-text-primary">{fmtC(invoice.amount)}</div>
        </div>
        <div className="card bg-surface p-4">
          <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Paid</div>
          <div className="text-xl font-heading text-green">{fmtC(invoice.paidAmount)}</div>
        </div>
        <div className="card bg-surface p-4">
          <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Balance</div>
          <div className="text-xl font-heading" style={{ color: statusColor }}>
            {fmtC(balance)}
          </div>
        </div>
      </div>

      {/* Covered Jobs (bulk invoice) — rendered from the lineItems snapshot so
          the breakdown survives even if a covered job is later deleted */}
      {isBulk && (
        <div>
          <div className="text-2xs font-semibold text-text-dim uppercase mb-2">
            Covered Jobs ({(invoice.lineItems || []).length || invoice.jobIds.length})
          </div>
          <div className="space-y-1">
            {(invoice.lineItems || invoice.jobIds.map(id => ({ jobId: id }))).map((li, i) => {
              const cj = allJobs.find(j => j.id === li.jobId);
              return (
                <div key={li.jobId || i} className="flex items-center justify-between gap-2 px-3 py-1.5 bg-surface rounded border border-border">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-text-primary truncate">{li.location || li.title || cj?.title || li.jobId}</div>
                    {(li.services || []).length > 0 && (
                      <div className="text-2xs text-text-dim truncate">
                        {li.services.map(s => s.desc).join(' · ')}
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-text-primary flex-shrink-0">{fmtC(li.amount)}</span>
                  {cj && (
                    <button
                      onClick={() => navigateToJob(navigate, cj.id)}
                      className="text-2xs text-accent hover:underline flex-shrink-0"
                      title="Go to job"
                    >
                      View →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Xero Sync Badge */}
      {xeroConnected && (invoice.xeroInvoiceId || !isHidden('invoice.xeroButtons')) && (
        <div className="bg-surface border border-border rounded p-3">
          {invoice.xeroInvoiceId ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-green flex items-center gap-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Synced to Xero
                </span>
                {invoice.xeroSyncedAt && (
                  <span className="text-2xs text-text-dim">{new Date(invoice.xeroSyncedAt).toLocaleDateString()}</span>
                )}
              </div>
              <div className="flex gap-2 text-2xs">
                <a
                  href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${invoice.xeroInvoiceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  View in Xero ↗
                </a>
                {invoice.xeroOnlineUrl && (
                  <a
                    href={invoice.xeroOnlineUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    Client Invoice Link ↗
                  </a>
                )}
              </div>
              {!isHidden('invoice.xeroButtons') && (
                <>
                  <button
                    data-focus="invoice.xeroButtons"
                    onClick={() => syncMutation.mutate(invoice.id)}
                    disabled={syncMutation.isPending}
                    className="text-2xs text-text-dim hover:text-text-primary disabled:opacity-50"
                  >
                    {syncMutation.isPending ? 'Syncing...' : 'Re-sync to Xero'}
                  </button>
                  <button
                    data-focus="invoice.xeroButtons"
                    onClick={async () => {
                      try {
                        const result = await pushInvoicePdfToXero({
                          invoice,
                          job: linkedJob,
                          settings,
                          uploadMutateAsync: uploadXeroPdfMutation.mutateAsync,
                        });
                        toast(`PDF uploaded to Xero (${result.filename})`, 'success');
                      } catch (err) {
                        toast(`PDF upload failed: ${err.message}`, 'error');
                      }
                    }}
                    disabled={uploadXeroPdfMutation.isPending || !invoice.xeroInvoiceId}
                    className="text-2xs text-text-dim hover:text-text-primary disabled:opacity-50"
                    title="Push the branded PDF to Xero as the downloadable file on the payment link"
                  >
                    {uploadXeroPdfMutation.isPending ? 'Uploading PDF...' : 'Re-upload PDF to Xero'}
                  </button>
                </>
              )}
            </div>
          ) : (
            <button
              data-focus="invoice.xeroButtons"
              onClick={() => syncMutation.mutate(invoice.id, {
                onSuccess: () => toast('Synced to Xero', 'success'),
                onError: (err) => toast(`Xero sync failed: ${err.message}`, 'error'),
              })}
              disabled={syncMutation.isPending}
              className="w-full px-3 py-1.5 bg-accent/10 border border-accent/30 text-accent text-xs font-semibold rounded hover:bg-accent/20 transition-colors disabled:opacity-50"
            >
              {syncMutation.isPending ? 'Pushing...' : 'Push to Xero'}
            </button>
          )}
        </div>
      )}

      {/* Invoice Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Terms</div>
          <p className="text-text-primary">{invoice.terms || '—'}</p>
        </div>
        <div>
          <div className="text-2xs font-semibold text-text-dim uppercase mb-1">Due Date</div>
          <p className="text-text-primary">{fmtD(invoice.dueDate) || '—'}</p>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div>
          <div className="text-2xs font-semibold text-text-dim uppercase mb-2">Notes</div>
          <p className="text-sm text-text-primary bg-surface p-3 rounded border border-border">{invoice.notes}</p>
        </div>
      )}

      {/* Payment Events Section */}
      {(payments.length > 0 || invoice.sentAt) && (
        <div>
          {/* Header with count badge */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Payment Events</h3>
            <span className="inline-flex items-center justify-center px-2 py-1 bg-accent/10 border border-accent/30 rounded text-2xs font-semibold text-accent">
              {(invoice.sentAt ? 1 : 0) + payments.length}
            </span>
          </div>

          {/* Progress bar */}
          {(parseFloat(invoice.amount) || 0) > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-2xs font-semibold text-text-dim">Invoice Paid</span>
                <span className="text-2xs font-semibold text-text-primary">
                  {Math.round(((parseFloat(invoice.paidAmount) || 0) / (parseFloat(invoice.amount) || 1)) * 100)}%
                </span>
              </div>
              <div className="w-full h-2 bg-surface rounded overflow-hidden border border-border">
                <div
                  className="h-full bg-green transition-all"
                  style={{ width: `${Math.min(((parseFloat(invoice.paidAmount) || 0) / (parseFloat(invoice.amount) || 1)) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="space-y-2">
            {/* Sent Event */}
            {invoice.sentAt && (
              <div className="flex gap-3 px-3 py-2 bg-surface rounded border border-border">
                <div className="flex flex-col items-center pt-1">
                  <div className="w-3 h-3 rounded-full bg-teal border-2 border-teal" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-text-primary font-semibold text-sm">Invoice Sent</div>
                  <div className="text-2xs text-text-dim">{fmtD(invoice.sentAt)}</div>
                </div>
              </div>
            )}

            {/* Payment Events */}
            {payments.map((p, i) => {
              // Calculate running balance after this payment (skip voided)
              const paidUpToAndIncluding = payments.slice(0, i + 1).filter(pay => !pay.voided).reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
              const runningBalance = (parseFloat(invoice.amount) || 0) - paidUpToAndIncluding;

              // Format payment method
              const methodLabels = {
                check: 'Check',
                ach: 'ACH Transfer',
                card: 'Credit Card',
                cash: 'Cash',
                other: 'Other',
              };
              const methodLabel = methodLabels[p.method] || p.method;

              return (
                <div key={i} className={`flex gap-3 px-3 py-2 bg-surface rounded border border-border ${p.voided ? 'opacity-50' : ''}`}>
                  <div className="flex flex-col items-center pt-1">
                    <div className={`w-3 h-3 rounded-full border-2 ${p.voided ? 'bg-red-accent border-red-accent' : 'bg-green border-green'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className={`text-text-primary font-semibold text-sm ${p.voided ? 'line-through' : ''}`}>{fmtC(p.amount)}</div>
                        <div className="text-2xs text-text-dim">{methodLabel}{p.voided ? ' — VOIDED' : ''}</div>
                      </div>
                      <div className="text-right flex-shrink-0 flex items-center gap-2">
                        <div>
                          {!p.voided && <div className="text-text-primary text-2xs font-semibold">Balance: {fmtC(Math.max(0, runningBalance))}</div>}
                          <div className="text-text-dim text-2xs">{fmtD(paymentDate(p))}</div>
                        </div>
                        {!p.voided && (
                          <FocusHide k="invoice.editMarkPaid">
                            <button
                              onClick={() => handleVoidPayment(i)}
                              disabled={updateMutation.isPending}
                              className="text-2xs text-red-accent hover:underline disabled:opacity-50 inline-flex items-center justify-center min-h-[40px] px-3 -my-2 md:min-h-0 md:p-0 md:my-0 md:inline"
                              title="Void this payment"
                            >
                              Void
                            </button>
                          </FocusHide>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Email Actions */}
      {invoice.client && (
        <div className="flex gap-2">
          <button
            onClick={async () => {
              const templateId = isBulk ? 'consolidated_invoice_sent'
                : invoice.invoiceType === 'deposit' ? 'deposit_invoice_sent' : 'invoice_sent';
              let effectiveInvoice = invoice;
              let syncFailed = false;
              // Bulk invoices have no job page, so JobDetail's send ceremony
              // (Xero sync for the payment link + branded PDF as the online
              // attachment) runs here before the email opens.
              if (isBulk && xeroConnected && !effectiveInvoice.xeroOnlineUrl) {
                try {
                  const result = await syncMutation.mutateAsync(invoice.id);
                  effectiveInvoice = {
                    ...effectiveInvoice,
                    xeroInvoiceId: result?.xeroInvoiceId || effectiveInvoice.xeroInvoiceId,
                    xeroOnlineUrl: result?.xeroOnlineUrl || effectiveInvoice.xeroOnlineUrl,
                  };
                } catch (err) {
                  syncFailed = true;
                  toast(`Xero sync failed — this invoice has no payment link yet: ${err.message}`, 'error');
                }
              }
              // First send: refresh dueDate + stamp sentAt so the client sees an
              // "as of today" date (deposits = today; others honor the invoice's
              // own terms so the stamped date never contradicts the shown terms,
              // falling back to the settings default when terms are unparseable).
              // On resend (sentAt already set), keep the original dueDate untouched.
              // A bulk send with a failed Xero sync is NOT a real send (the email
              // carries no payment link) — no sentAt stamp, no job flips.
              if (!invoice.sentAt && !(isBulk && syncFailed)) {
                const newDueDate = dueDateInDays(invoice.invoiceType === 'deposit'
                  ? 0
                  : (parseTermsDays(invoice.terms) ?? finalTermsDays(settings)));
                const patch = {
                  id: invoice.id,
                  sentAt: new Date().toISOString(),
                  dueDate: newDueDate,
                  ...(invoice.status === 'draft' ? { status: 'sent' } : {}),
                };
                try {
                  await updateMutation.mutateAsync(patch);
                  effectiveInvoice = { ...effectiveInvoice, ...patch };
                  if (xeroConnected && effectiveInvoice.xeroInvoiceId) syncMutation.mutate(invoice.id);
                  // First send of a bulk invoice moves its delivered jobs to invoiced.
                  await flipCoveredJobs('invoiced');
                } catch (err) {
                  toast('Failed to stamp send date — opening email with existing date', 'error');
                }
              }
              // Branded PDF goes up AFTER the due-date stamp so the attached
              // document shows the same due date as the email.
              if (isBulk && xeroConnected && effectiveInvoice.xeroInvoiceId) {
                try {
                  await pushInvoicePdfToXero({
                    invoice: effectiveInvoice,
                    job: null,
                    settings,
                    uploadMutateAsync: uploadXeroPdfMutation.mutateAsync,
                  });
                } catch (err) {
                  toast(`Couldn't upload PDF to Xero: ${err.message}`, 'warning');
                }
              }
              const { subject, body } = buildEmail(templateId, { invoice: effectiveInvoice, job: linkedJob, settings, senderName });
              // On resend (sentAt was already set before this click), flag the subject so
              // the client can distinguish a follow-up from the original send.
              const finalSubject = invoice.sentAt ? `(REMINDER) ${subject}` : subject;
              window.location.href = buildMailtoUrl(clientEmail, finalSubject, body);
            }}
            className="flex-1 px-3 py-2 bg-accent/10 border border-accent/30 text-accent text-xs font-semibold rounded hover:bg-accent/20 transition-colors"
          >
            {isBulk ? 'Send Bulk Invoice' : invoice.invoiceType === 'deposit' ? 'Send Deposit Invoice' : 'Send Invoice Email'}
          </button>
          {balance > 0 && invoice.status !== 'draft' && (
            <button
              onClick={() => openEmail('payment_reminder', { toEmail: clientEmail, invoice, settings, senderName })}
              className="flex-1 px-3 py-2 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors"
            >
              Payment Reminder
            </button>
          )}
        </div>
      )}

      {/* Download PDF */}
      <FocusHide k="invoice.pdfButtons">
        <div className="flex gap-2">
          <button
            onClick={async () => {
              try {
                const r = await generateInvoicePdf({ invoice, settings, returnBlob: true });
                setPdfPreview(r);
              } catch (err) {
                console.error('PDF preview failed:', err);
                toast('Failed to generate PDF', 'error');
              }
            }}
            className="flex-1 px-3 py-2 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors"
          >
            Preview
          </button>
          <button
            onClick={async () => {
              try {
                await generateInvoicePdf({ invoice, settings });
                toast('Invoice PDF downloaded');
              } catch (err) {
                console.error('PDF generation failed:', err);
                toast('Failed to generate PDF', 'error');
              }
            }}
            className="flex-1 px-3 py-2 bg-accent/10 border border-accent/30 text-accent text-xs font-semibold rounded hover:bg-accent/20 transition-colors"
          >
            Download PDF
          </button>
        </div>
      </FocusHide>

      <PdfPreviewModal
        open={!!pdfPreview}
        blob={pdfPreview?.blob}
        filename={pdfPreview?.filename}
        onClose={() => setPdfPreview(null)}
      />

      {/* Xero Online Invoice Link */}
      {invoice.xeroOnlineUrl && (
        <a
          href={invoice.xeroOnlineUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full px-3 py-2 bg-accent/10 border border-accent/30 text-accent text-xs font-semibold rounded hover:bg-accent/20 transition-colors text-center"
        >
          Xero Online Invoice ↗
        </a>
      )}

      {/* Record Payment Form */}
      {showPaymentForm ? (
        <form data-focus="invoice.recordPayment" onSubmit={handleRecordPayment} className="bg-surface border border-border rounded p-4 space-y-3">
          <div>
            <label className="block text-2xs font-semibold text-text-dim mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={balance}
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              placeholder="0.00"
              className="w-full px-2 py-1.5 bg-bg border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
            />
          </div>
          <div>
            <label className="block text-2xs font-semibold text-text-dim mb-1">Method</label>
            <select
              value={paymentForm.method}
              onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
              className="w-full px-2 py-1.5 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
            >
              <option value="check">Check</option>
              <option value="ach">ACH</option>
              <option value="card">Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-2xs font-semibold text-text-dim mb-1">Date</label>
            <input
              type="date"
              value={paymentForm.date}
              onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
              className="w-full px-2 py-1.5 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1 px-3 py-1.5 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              Record
            </button>
            <button
              type="button"
              onClick={() => setShowPaymentForm(false)}
              className="flex-1 px-3 py-1.5 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <FocusHide k="invoice.recordPayment">
          <button
            onClick={() => setShowPaymentForm(true)}
            disabled={balance <= 0}
            className="w-full px-3 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Record Payment
          </button>
        </FocusHide>
      )}

      {/* Status Actions */}
      {!statusActionsEmpty && (
        <div className="flex gap-2 pt-2 border-t border-border">
          {invoice.status !== 'sent' && invoice.status !== 'paid' && (
            <FocusHide k="invoice.markSent">
              <button
                onClick={() => handleStatusChange('sent')}
                disabled={updateMutation.isPending}
                className="flex-1 px-3 py-1.5 bg-teal/10 border border-teal/30 text-teal text-xs font-semibold rounded hover:bg-teal/20 transition-colors disabled:opacity-50"
              >
                Mark Sent
              </button>
            </FocusHide>
          )}
          {invoice.status !== 'paid' && balance <= 0 && (
            <FocusHide k="invoice.editMarkPaid">
              <button
                onClick={() => handleStatusChange('paid')}
                disabled={updateMutation.isPending}
                className="flex-1 px-3 py-1.5 bg-green/10 border border-green/30 text-green text-xs font-semibold rounded hover:bg-green/20 transition-colors disabled:opacity-50"
              >
                Mark Paid
              </button>
            </FocusHide>
          )}
          {invoice.status !== 'void' && (
            <button
              onClick={() => handleStatusChange('void')}
              disabled={updateMutation.isPending}
              className="flex-1 px-3 py-1.5 bg-red-accent/10 border border-red-accent/30 text-red-accent text-xs font-semibold rounded hover:bg-red-accent/20 transition-colors disabled:opacity-50"
            >
              Void
            </button>
          )}
        </div>
      )}

      {/* Delete */}
      {canDelete && (
      <FocusHide k="invoice.delete">
        <button
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className={`w-full px-3 py-1.5 text-xs font-semibold rounded transition-colors disabled:opacity-50 ${
            invoice.xeroInvoiceId
              ? 'bg-surface border border-border text-text-dim cursor-not-allowed'
              : 'bg-red-accent/10 border border-red-accent/30 text-red-accent hover:bg-red-accent/20'
          }`}
        >
          {invoice.xeroInvoiceId ? 'Delete (use Void for synced)' : 'Delete Invoice'}
        </button>
      </FocusHide>
      )}
    </div>
  );
}
